import paper from "paper";
import type { ConstructionLine } from "./Project";
import { computeBezierCycleStep } from "../tools/bezierCycleLogic";

/** Advance the anchor at `(pathIndex, segmentIndex)` of `line` one
 *  step through the four-state bezier cycle:
 *    0 = no-curve   (no entry, both handles zero)
 *    1 = independent (no entry, at least one handle non-zero)
 *    2 = angle      (entry mode "angle")
 *    3 = smooth     (entry mode "smooth")
 *  Endpoint anchors of an open subpath collapse to a 0 ↔ 1 toggle
 *  since their dead-side handle has nothing to mirror against.
 *  Returns the mutated `pathData` + `smoothAnchors` plus a history
 *  label, leaving the caller to commit the change. Returns `null`
 *  when the line / segment can't be parsed. */
/** The slice of an outline-bearing model the cycle needs — satisfied
 *  by ConstructionLine and (since #969) by Piece outlines. */
export type BezierCycleModel = Pick<
  ConstructionLine,
  "pathData" | "smoothAnchors" | "bezierMemory"
>;

export function cycleAnchorBezier(
  line: BezierCycleModel,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): {
  pathData: string;
  smoothAnchors: ConstructionLine["smoothAnchors"];
  bezierMemory: ConstructionLine["bezierMemory"];
  label: string;
} | null {
  let tmp: paper.CompoundPath;
  try {
    tmp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    tmp.children && tmp.children.length > 0
      ? (tmp.children as paper.Path[])
      : [tmp as unknown as paper.Path];
  const sub = subpaths[pathIndex];
  const seg = sub?.segments[segmentIndex];
  if (!sub || !seg) {
    tmp.remove();
    return null;
  }

  const existing = line.smoothAnchors ?? [];
  const currentEntry = existing.find(
    (a) => a.pathIndex === pathIndex && a.segmentIndex === segmentIndex,
  );
  const currentMode: "angle" | "smooth" | null = currentEntry
    ? (currentEntry.mode ?? "smooth")
    : null;
  const isEndpoint =
    !sub.closed &&
    (segmentIndex === 0 || segmentIndex === sub.segments.length - 1);

  // All bezier-cycle decisions live in `computeBezierCycleStep` —
  // a paper-free pure function that takes the current handles +
  // mode + memory entry and returns the new handles + mode + memory
  // entry. The wrapper here just (a) extracts inputs from the
  // paper.Segment, (b) computes the bootstrap tangent for the
  // 0 → 1 case, (c) writes the result back, and (d) re-serialises
  // pathData via paper.
  const memory = line.bezierMemory ?? [];
  const memEntry = memory.find(
    (m) => m.pathIndex === pathIndex && m.segmentIndex === segmentIndex,
  );
  const otherMemory = memory.filter(
    (m) => !(m.pathIndex === pathIndex && m.segmentIndex === segmentIndex),
  );
  const tangent = computeSmoothTangent(sub, segmentIndex, scope);
  const mag = computeDefaultHandleMagnitude(sub, segmentIndex);
  const step = computeBezierCycleStep({
    handleIn: { x: seg.handleIn.x, y: seg.handleIn.y },
    handleOut: { x: seg.handleOut.x, y: seg.handleOut.y },
    currentMode,
    isEndpoint,
    isFirstEndpoint: segmentIndex === 0,
    bootstrapTangent: { x: tangent.x, y: tangent.y },
    bootstrapMagnitude: mag,
    memory: memEntry
      ? {
          handleIn: memEntry.handleIn,
          handleOut: memEntry.handleOut,
          ...(memEntry.preLock ? { preLock: memEntry.preLock } : {}),
          ...(memEntry.preMirror ? { preMirror: memEntry.preMirror } : {}),
        }
      : undefined,
  });

  seg.handleIn = new scope.Point(step.handleIn.x, step.handleIn.y);
  seg.handleOut = new scope.Point(step.handleOut.x, step.handleOut.y);

  const nextPathData = tmp.pathData;
  tmp.remove();

  let nextAnchors: ConstructionLine["smoothAnchors"];
  if (step.mode === null) {
    const filtered = existing.filter(
      (a) => !(a.pathIndex === pathIndex && a.segmentIndex === segmentIndex),
    );
    nextAnchors = filtered.length > 0 ? filtered : undefined;
  } else {
    nextAnchors = [
      ...existing.filter(
        (a) => !(a.pathIndex === pathIndex && a.segmentIndex === segmentIndex),
      ),
      { pathIndex, segmentIndex, mode: step.mode },
    ];
  }

  const nextMemory: NonNullable<ConstructionLine["bezierMemory"]> = step.memory
    ? [
        ...otherMemory,
        {
          pathIndex,
          segmentIndex,
          handleIn: step.memory.handleIn,
          handleOut: step.memory.handleOut,
          ...(step.memory.preLock ? { preLock: step.memory.preLock } : {}),
          ...(step.memory.preMirror
            ? { preMirror: step.memory.preMirror }
            : {}),
        },
      ]
    : otherMemory;

  return {
    pathData: nextPathData,
    smoothAnchors: nextAnchors,
    bezierMemory: nextMemory.length > 0 ? nextMemory : undefined,
    label: step.label,
  };
}

/** Pick the natural "smooth tangent" direction at `segmentIndex` of
 *  `sub`: the average of the unit vectors along the incoming and
 *  outgoing edges. This is the direction the curve would have if
 *  the anchor were smooth. Falls back to (1, 0) when both
 *  neighbours coincide (degenerate corner). */
function computeSmoothTangent(
  sub: paper.Path,
  segmentIndex: number,
  scope: paper.PaperScope,
): paper.Point {
  const segs = sub.segments;
  const n = segs.length;
  const seg = segs[segmentIndex]!;
  const prevSeg: paper.Segment | null = sub.closed
    ? segs[(segmentIndex - 1 + n) % n]!
    : segmentIndex > 0
      ? segs[segmentIndex - 1]!
      : null;
  const nextSeg: paper.Segment | null = sub.closed
    ? segs[(segmentIndex + 1) % n]!
    : segmentIndex < n - 1
      ? segs[segmentIndex + 1]!
      : null;
  let tx = 0;
  let ty = 0;
  if (prevSeg) {
    const dx = seg.point.x - prevSeg.point.x;
    const dy = seg.point.y - prevSeg.point.y;
    const len = Math.hypot(dx, dy);
    if (len > 0) {
      tx += dx / len;
      ty += dy / len;
    }
  }
  if (nextSeg) {
    const dx = nextSeg.point.x - seg.point.x;
    const dy = nextSeg.point.y - seg.point.y;
    const len = Math.hypot(dx, dy);
    if (len > 0) {
      tx += dx / len;
      ty += dy / len;
    }
  }
  const tlen = Math.hypot(tx, ty);
  if (tlen === 0) return new scope.Point(1, 0);
  return new scope.Point(tx / tlen, ty / tlen);
}

/** Pick a small handle magnitude for a freshly-curved anchor: 25%
 *  of the nearest neighbour distance, clamped to [2, 6] mm so the
 *  handles read as a hint, not as the dominant feature of the
 *  curve. */
function computeDefaultHandleMagnitude(
  sub: paper.Path,
  segmentIndex: number,
): number {
  const segs = sub.segments;
  const n = segs.length;
  const seg = segs[segmentIndex]!;
  const prevSeg: paper.Segment | null = sub.closed
    ? segs[(segmentIndex - 1 + n) % n]!
    : segmentIndex > 0
      ? segs[segmentIndex - 1]!
      : null;
  const nextSeg: paper.Segment | null = sub.closed
    ? segs[(segmentIndex + 1) % n]!
    : segmentIndex < n - 1
      ? segs[segmentIndex + 1]!
      : null;
  let minDist = Infinity;
  if (prevSeg) {
    minDist = Math.min(
      minDist,
      Math.hypot(seg.point.x - prevSeg.point.x, seg.point.y - prevSeg.point.y),
    );
  }
  if (nextSeg) {
    minDist = Math.min(
      minDist,
      Math.hypot(seg.point.x - nextSeg.point.x, seg.point.y - nextSeg.point.y),
    );
  }
  if (!isFinite(minDist) || minDist === 0) return 4;
  return Math.max(2, Math.min(6, minDist * 0.25));
}

/** Build the initial `smoothAnchors` array for a freshly-committed
 *  construction line. Walks the parsed pathData and, for every
 *  anchor with non-zero handles on BOTH sides that are anti-parallel
 *  (collinear, 180° apart), records a lock entry:
 *   - `mode: "smooth"` when the magnitudes match (fully mirrored).
 *   - `mode: "angle"` when the magnitudes differ (collinear, free
 *     to grow / shrink independently).
 *  Anchors whose handles aren't anti-parallel (true corners, or
 *  one-sided handles) and entirely-straight anchors get no entry,
 *  so the user can drag either side without forcing the other to
 *  jump.
 *
 *  This matters most for `path.simplify()` output (freehand commit):
 *  Schneider's algorithm fits handles independently per side, so
 *  most anchors come out anti-parallel-but-unequal — picking
 *  `"smooth"` blindly would snap them equal on the first drag.
 *
 *  Returns `undefined` when no anchor qualifies, so we don't
 *  attach an empty array to the model. */
export function computeDefaultSmoothAnchors(
  pathData: string,
  scope: paper.PaperScope,
): NonNullable<ConstructionLine["smoothAnchors"]> | undefined {
  let path: paper.CompoundPath;
  try {
    path = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return undefined;
  }
  const subpaths: paper.Path[] =
    path.children && path.children.length > 0
      ? (path.children as paper.Path[])
      : [path as unknown as paper.Path];
  const anchors: NonNullable<ConstructionLine["smoothAnchors"]> = [];
  // Absolute fuzz for "is this handle effectively zero" — matches
  // the same value bezierCycleLogic uses for handle-equality checks.
  const ZERO = 0.001;
  // Relative fuzz for parallelism / magnitude equality. Generous
  // enough to absorb `path.simplify` rounding (~1e-3 of magnitude)
  // without misclassifying user-bent corners.
  const REL = 0.01;
  subpaths.forEach((sub, pathIndex) => {
    const segCount = sub.segments.length;
    sub.segments.forEach((seg, segmentIndex) => {
      // Endpoints of an open subpath have no neighbour on one side,
      // so locking them — even if `path.simplify` leaves a stub
      // handle pointing into the curve — would force a phantom
      // mirror jump when the user drags the one real handle. Always
      // leave open-path endpoints independent.
      if (
        !sub.closed &&
        (segmentIndex === 0 || segmentIndex === segCount - 1)
      ) {
        return;
      }
      const inX = seg.handleIn.x;
      const inY = seg.handleIn.y;
      const outX = seg.handleOut.x;
      const outY = seg.handleOut.y;
      const inMag = Math.hypot(inX, inY);
      const outMag = Math.hypot(outX, outY);
      // Straight corner or one-sided handle → leave independent.
      if (inMag < ZERO || outMag < ZERO) return;
      // sin(angle-between) ≈ |cross| / (|in| * |out|). Treat as
      // anti-parallel when the deviation from a straight 180° tangent
      // is small AND the dot is negative (opposite directions).
      const cross = inX * outY - inY * outX;
      const dot = inX * outX + inY * outY;
      const sinDev = Math.abs(cross) / (inMag * outMag);
      if (sinDev > REL || dot >= 0) return;
      // Anti-parallel. "Smooth" when magnitudes also match within
      // the same relative tolerance; otherwise "angle".
      const magsEqual =
        Math.abs(inMag - outMag) / Math.max(inMag, outMag) < REL;
      anchors.push({
        pathIndex,
        segmentIndex,
        mode: magsEqual ? "smooth" : "angle",
      });
    });
  });
  path.remove();
  return anchors.length > 0 ? anchors : undefined;
}
